import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import BottomSheet from "../components/BottomSheet";
import { useApp } from "../Contex";
import {
    fetchZones, getMenuAddons,
    getMenuDetails, getMenuHours,
    getMenuItems, getMenuZones, saveMenuAddons, saveMenuHours, updateCache,
    updateItem,
    updateMenu,
} from "../api/api";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import IngredientsTab from "./tabs/IngridientsTab";
import TabModal from "../components/TabModal";

export default function SpecificMenu() {
    const { slug } = useParams();
    const { refresh, permissions: { write } } = useApp();

    const [menuDetails, setMenuDetails] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [isMenuChanged, setIsMenuChanged] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [fetching, setFetching] = useState(false);

    const fetchMenu = useCallback(async () => {
        setFetching(true)
        const detailsRes = await getMenuDetails(slug);
        if (detailsRes.ok) {
            setMenuDetails(detailsRes.details);
            setIsMenuChanged(false);
        }
        const itemsRes = await getMenuItems(slug);
        if (itemsRes.ok) {
            setMenuItems(itemsRes.items);
        }
        setFetching(false)
    }, [slug]);

    useEffect(() => {
        fetchMenu();
    }, [fetchMenu, refresh]);

    const handleMenuField = (field) => (e) => {
        const value = e.target.value;
        setMenuDetails(prev => ({
            ...prev,
            [field]: value === ""
                ? ""
                : field === "sort"
                    ? String(Math.max(0, Math.min(999, Number(value))))
                    : value
        }));
        setIsMenuChanged(true);
    };

    const saveMenu = async () => {
        const res = await updateMenu(menuDetails.mnuid, menuDetails);
        if (res.ok) fetchMenu();
    };

    const toggleHiddenMenu = async () => {
        const sort = Number(menuDetails.sort);
        const newSort = sort < 900 ? (sort > 99 ? 900 : sort + 900) : sort - 900;
        await updateMenu(menuDetails.mnuid, { ...menuDetails, sort: String(newSort) });
        fetchMenu();
    };

    return (
        <div>
            {menuDetails && (
                <MenuHeader
                    details={menuDetails}
                    write={write}
                    changed={isMenuChanged}
                    onFieldChange={handleMenuField}
                    onSave={saveMenu}
                    onToggleHidden={toggleHiddenMenu}
                    refetch={fetchMenu}
                />
            )}

            <div className="mt-4">
                {fetching && menuItems.length === 0 && (
                    <div className="d-flex justify-content-center my-3">
                        <div className="spinner-border text-warning" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}

                {menuItems.map(item => (
                    <MenuItem
                        key={item.plu}
                        item={item}
                        write={write}
                        onEdit={() => setSelectedItem(item)}
                        onToggleHidden={async () => {
                            const sort = Number(item.sort);
                            const newSort = sort < 900 ? (sort > 99 ? 900 : sort + 900) : sort - 900;
                            await updateItem(item.plu, { ...item, sort: String(newSort) });
                            fetchMenu();
                        }}
                    />
                ))}
            </div>

            <BottomSheet show={!!selectedItem} setShow={() => setSelectedItem(null)} onClose={fetchMenu}>
                {selectedItem && (
                    <ItemModal
                        item={selectedItem}
                        write={write}
                        refresh={fetchMenu}
                    />
                )}
            </BottomSheet>
        </div>
    );

    // ----- Sub-components ----- //

    function MenuHeader({ details, write, changed, onFieldChange, onSave, onToggleHidden, refetch }) {
        const [handleZones, setHandleZones] = useState(false)
        const [handleAddons, setHandleAddons] = useState(false)
        const [handleHours, setHandleHourse] = useState(false)
        const hidden = Number(details.sort) > 899;
        return (
            <div className="card p-4 shadow-sm mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <h2 className="mb-0">{details.text}</h2>
                        {write && (
                            <div className="d-flex align-items-center gap-2">
                                <button className="btn btn-outline-secondary" onClick={onToggleHidden} title="Förhandsgranska">
                                    <i className={`bi ${hidden ? 'bi-eye-slash text-danger' : 'bi-eye'}`}></i>
                                </button>
                                <button className="btn btn-secondary" onClick={()=>{
                                    updateCache(details.mnuid)
                                }}>
                                    <span>Cache </span>
                                    <i className="bi bi-arrow-clockwise"/>
                                </button>
                            </div>
                        )}
                    </div>
                    {changed && write && (
                        <button className="btn btn-success" onClick={onSave}>
                            <i className="bi bi-save me-2"></i>Spara meny
                        </button>
                    )}
                </div>
                <div className="mt-3">
                    <label className="form-label">Sortering</label>
                    <input
                        type="number"
                        className="form-control w-auto"
                        value={details.sort}
                        onChange={onFieldChange('sort')}
                        disabled={!write}
                    />
                </div>
                {write &&
                    <>
                        <div className="mt-2">
                            <button className="btn btn-secondary mt-2 px-3" onClick={() => {setHandleZones(true)}}>
                                Hantera zoner
                            </button>
                        </div>
                        <div>
                            <button className="btn btn-outline-secondary mt-2 px-3" onClick={()=>{setHandleHourse(true)}}>
                                Hantera öppettider
                            </button>
                        </div>
                        <div>
                            <button className="btn btn-secondary mt-2 px-3" onClick={() => {setHandleAddons(true)}}>
                                Hantera tillval
                            </button>
                        </div>
                        <HourModal show={handleHours} setShow={setHandleHourse} gid={details.gid}/>
                        <AddonModal show={handleAddons} setShow={setHandleAddons} item={details} refresh={refetch}/>
                        <ZoneModal show={handleZones} setShow={setHandleZones} Indata={details} reFetch={refetch}/>
                    </>}
            </div>
        );
    }

    function MenuItem({item, write, onEdit, onToggleHidden }) {
        const hidden = Number(item.sort) > 899;
        return (
            <div className={`card mb-2 p-2 ${hidden ? 'bg-danger-subtle' : 'bg-light'}`}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-1">{item.text.toSwe()}</h5>
                        {!hidden && <small>Sort: {item.sort}</small>}
                    </div>
                    {write && (
                        <div className="d-flex gap-2">
                            <button className="btn btn-outline-secondary" onClick={onEdit}>
                                <i className="bi bi-pencil"></i>
                            </button>
                            <button className={`btn ${hidden ? 'btn-outline-danger' : 'btn-outline-secondary'}`} onClick={onToggleHidden}>
                                <i className={`bi ${hidden ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function AddonModal({item, show, setShow}){
        const [T, setT] = useState([])
        const [S, setS] = useState([])

        useEffect(() => {
            refresh()
        }, [item]);

        function refresh(){
            getMenuAddons(item.mnuid).then((res) => {
                if(res.ok){
                    setT(res.data.T)
                    setS(res.data.S)
                }
            })
        }


        const tabs = [
            {name: "Tillval", content:<TPage data={T}/>},
            {name: "Sides", content:<SPage data={S}/>},
        ]

        const updateSortAndSave = (item, newSort) => {
            const updatedItem = { ...item, m0sort: newSort.toString() };
            saveMenuAddons(updatedItem).then(res => {
                if (res.ok) refresh();
            });
        };

        return <TabModal tabs={tabs} show={show} setShow={setShow}/>


        function TPage({ data }) {
            const [addons, setAddons] = useState({ level1: [], level2: [], level3: [] });

            useEffect(() => {
                if (!data) return;

                const level1 = [], level2 = [], level3 = [];

                for (let i = 0; i < data.length; i++) {
                    const item = data[i];
                    if (item.m0sort < 600) {
                        level1.push(item);
                    } else if (item.m0sort < 900) {
                        level2.push(item);
                    } else {
                        level3.push(item);
                    }
                }

                setAddons({ level1, level2, level3 });
            }, [data]);

            const visibilityOptions = [
                { label: "Menynivå", value: 0 },
                { label: "Produktnivå", value: 600 },
                { label: "Dold", value: 900 }
            ];

            const getCurrentLevelValue = (m0sort) => {
                const sort = parseInt(m0sort);
                if (sort < 600) return 0;
                if (sort < 900) return 600;
                return 900;
            };


            const AddonItem = ({ item, level }) => {
                const bgMap = {
                    level1: "white",
                    level2: "secondary-subtle",
                    level3: "danger-subtle"
                };

                const handleChange = (e) => {
                    const newSort = parseInt(e.target.value);
                    if (newSort !== getCurrentLevelValue(item.m0sort)) {
                        updateSortAndSave(item, newSort);
                    }
                };

                return (
                    <div className={`card p-2 mb-2 bg-${bgMap[level]} d-flex flex-row justify-content-between align-items-center`}>
                        <span className="fw-semibold">{item.m0text.toSwe()}</span>
                        <select
                            className="form-select w-auto ms-3"
                            value={getCurrentLevelValue(item.m0sort)}
                            onChange={handleChange}
                        >
                            {visibilityOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                );
            };



            return (
                <>
                    <h5 className="mt-4">Menynivå</h5>
                    {addons.level1.map(item => <AddonItem key={item.m0katnr} item={item} level="level1" />)}

                    <h5 className="mt-4">Produktnivå</h5>
                    {addons.level2.map(item => <AddonItem key={item.m0katnr} item={item} level="level2" />)}

                    <h5 className="mt-4">Dold</h5>
                    {addons.level3.map(item => <AddonItem key={item.m0katnr} item={item} level="level3" />)}
                </>
            );
        }

        function SPage({data}){
            const [sides, setSides] = useState({visible: [], hidden: []})

            useEffect(() => {
                if (!data) return;

                const visible = [], hidden = [];

                for (let i = 0; i < data.length; i++) {
                    const item = data[i];
                    if (item.m0sort < 900) {
                        visible.push(item);
                    } else {
                        hidden.push(item);
                    }
                }



                setSides({ visible, hidden });
            }, [data]);

            const SideItem = ({item, hidden}) => {
                return <div
                    className={`card p-2 mb-2 ${hidden && 'bg-danger-subtle'} d-flex flex-row justify-content-between align-items-center`}>
                    <span className="fw-semibold">{item.m0text.toSwe()}</span>
                    <button className={`btn btn-${hidden ? 'outline-danger' : 'outline-secondary'}`} onClick={()=>{
                        const {m0sort} = item;
                        if(m0sort <900){
                            updateSortAndSave(item, 900)
                        }else{
                            updateSortAndSave(item, 0)
                        }
                    }}>
                        <i className={`bi bi-eye${hidden ? '-slash': ''}`}/>
                    </button>
                </div>
            }

            return (
                <>
                    <h5 className="mt-4">Synliga</h5>
                    {sides.visible.map(item => <SideItem key={item.m0katnr} item={item} hidden={false}/>)}
                    <h5 className="mt-4">Dolda</h5>
                    {sides.hidden.map(item => <SideItem key={item.m0katnr} item={item} hidden={true}/>)}
                </>)
        }
    }

    function ItemModal({item: initialItem, write, refresh}) {
        const [activeTab, setActiveTab] = useState('visibility');
        const [localItem, setLocalItem] = useState(initialItem);
        const [isChanged, setIsChanged] = useState(false);

        useEffect(() => {
            setLocalItem(initialItem);
            setIsChanged(false);
            setActiveTab('visibility');
        }, [initialItem]);

        const handleField = (field) => (e) => {
            const val = e.target.value;
            setLocalItem(prev => ({
                ...prev,
                [field]: field === 'sort'
                    ? String(Math.max(0, Math.min(999, Number(val))))
                    : val
            }));
            setIsChanged(true);
        };

        const saveItem = async () => {
            const res = await updateItem(localItem.plu, localItem);
            if (res.ok) {
                setIsChanged(false)
                refresh()
            }
        };

        return (
            <div>
                <div className="d-flex mb-3">
                    <Tab label="Synlighet" active={activeTab==='visibility'} onClick={()=>setActiveTab('visibility')} />
                    <Tab label="Ingredienser" active={activeTab==='ingredients'} onClick={()=>setActiveTab('ingredients')} />
                </div>
                {activeTab === 'visibility' && (
                    <VisibilityTab
                        item={localItem}
                        write={write}
                        changed={isChanged}
                        onFieldChange={handleField}
                        onToggleHidden={() => updateItem(localItem.plu, { ...localItem, sort: String(Number(localItem.sort) < 900 ? 900 : 0) }).then(()=>refresh())}
                        onSave={saveItem}
                    />
                )}
                {activeTab === 'ingredients' && (
                    <IngredientsTab item={localItem} refresh={refresh} write={write} />
                )}
            </div>
        );
    }

    function ZoneModal({show, setShow, Indata, reFetch}){
        const [data, setData] = useState([]);
        const [options, setOptions] = useState([])
        const {permissions:{write}} = useApp()
        const baseZone = {zoneid: "", ftid: "0000", ttid: "0000"}

        const [newZone, setNewZone] = useState(baseZone)

        const [changed, setChanged] = useState(false)

        useEffect(() => {
            setData(Indata?.zones??[])
            fetchZones().then(res=>{
                if(res.ok){
                    setOptions(res.zones)
                }
            })
        }, [Indata]);

        const selectOptions = options.map(zone => ({
            label: zone.text,
            value: zone.zone
        }));

        return <BottomSheet show={show} setShow={setShow} onClose={reFetch}>
            <div>
                <h4>Regiera zoner</h4>
                <hr/>
                <div className="overflow-y-scroll" style={{maxHeight: "75dvh"}}>
                    {data.map((zone, idx) => {

                        const {zoneid, ftid, ttid} = zone
                        const item = options.find((zone)=>zone.zone === zoneid)

                        return (
                            <div key={idx} className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                <span className="fs-5 fw-semibold text-nowrap px-2 text-start">{item?.text ?? zoneid}</span>
                                <input className="flex-grow-1 form-control" type="time"
                                       value={`${ftid.substring(0,2)}:${ftid.substring(2)}`}
                                       onChange={(e) => {
                                           const value = e.target.value;
                                           const formatted = value.replace(":", "");
                                           setData(prev => {
                                               const updated = [...prev];
                                               updated[idx] = { ...updated[idx], ftid: formatted };
                                               return updated;
                                           });
                                           setChanged(true)
                                       }}


                                />
                                <span className="fs-5 fw-bold">-</span>
                                <input className="flex-grow-1 form-control" type="time"
                                       value={`${ttid.substring(0,2)}:${ttid.substring(2)}`}
                                       onChange={(e) => {
                                           const value = e.target.value;
                                           const formatted = value.replace(":", "");
                                           setData(prev => {
                                               const updated = [...prev];
                                               updated[idx] = { ...updated[idx], ttid: formatted };
                                               return updated;
                                           });
                                           setChanged(true)
                                       }}
                                />
                                <button className="btn btn-outline-danger" onClick={()=>{
                                    setData(prevState => {
                                        const updated = [...prevState]
                                        updated.splice(idx, 1)
                                        return updated;
                                    })
                                    setChanged(true)
                                }}>
                                    <i className="bi bi-trash" />
                                </button>
                            </div>
                        )
                    })}
                    {write &&
                        <div className="text-end mt-3" style={{minHeight: "38px"}}>
                            {changed &&
                                <button className="btn btn-success" onClick={()=>{
                                    updateMenu(Indata.mnuid, ({...Indata, zones: data})).then(res=>{
                                        setChanged(!res.ok)
                                    })
                                }}>
                                    <i className="bi bi-save me-2"/>Spara ändringar
                                </button>
                            }
                        </div>
                    }
                    <hr/>
                </div>
                {write &&
                    <>
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="flex-grow-1">
                                <Select
                                    placeholder="Välj zon..."
                                    options={selectOptions}
                                    value={selectOptions.find(opt => opt.value === newZone.zoneid) ?? null}
                                    onChange={({value}) => {
                                        setNewZone(prev => ({...prev, zoneid: value}));
                                    }}
                                />
                            </div>
                            <input className="form-control w-auto" type="time"
                                   onChange={(e) => {
                                       const value = e.target.value;
                                       const ftid = value.replace(":", "")
                                       setNewZone(prevState => ({...prevState, ftid}))
                                   }}
                                   value={`${newZone.ftid.substring(0, 2)}:${newZone.ftid.substring(2)}`}
                            />
                            <input className="form-control w-auto" type="time"
                                   onChange={(e) => {
                                       const value = e.target.value;
                                       const ttid = value.replace(":", "")
                                       setNewZone(prevState => ({...prevState, ttid}))
                                   }}
                                   value={`${newZone.ttid.substring(0, 2)}:${newZone.ttid.substring(2)}`}
                            />
                        </div>
                        <div className="text-center mt-2">
                            <button className="btn btn-secondary w-100"
                                    disabled={newZone.ftid === "" || newZone.ttid === "" || newZone.zoneid === ""}
                                    onClick={() => {
                                        setData(prevState => ([...prevState, newZone]))
                                        setNewZone(baseZone)
                                        setChanged(true)
                                    }}
                            >
                                Lägg till
                            </button>
                        </div>
                    </>
                }

            </div>
        </BottomSheet>
    }

    function HourModal({ show, setShow, gid }) {
        const [hours, setHours] = useState({});
        const [changed, setChanged] = useState(false);

        useEffect(() => {
            if (!show) return;
            getMenuHours(gid).then(res => {
                if (res.ok) {
                    setHours(res.data);
                }
            });
        }, [gid, show]);

        const saveHours = () => {
            saveMenuHours(gid, hours).then(res => {
                if (res.ok) {
                    setShow(false);
                }
            });
        };

        const updateNestedValue = (path, value) => {
            setHours(prev => {
                const parts = path.split(".");
                const updated = { ...prev };
                let current = updated;
                for (let i = 0; i < parts.length - 1; i++) {
                    current[parts[i]] = { ...current[parts[i]] };
                    current = current[parts[i]];
                }
                current[parts.at(-1)] = value;
                return updated;
            });
            setChanged(true);
        };

        const clearNestedValue = (path) => {
            setHours(prev => {
                const parts = path.split(".");
                const updated = { ...prev };
                let current = updated;
                for (let i = 0; i < parts.length - 1; i++) {
                    current[parts[i]] = { ...current[parts[i]] };
                    current = current[parts[i]];
                }
                delete current[parts.at(-1)];
                return updated;
            });
            setChanged(true);
        };

        function TimeRangeInput({ title = null, value, path }) {
            const toTimeInputFormat = (hhmm) => {
                if (!hhmm || hhmm.length !== 4) return '';
                return hhmm === '2400' ? '00:00' : `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
            };

            const fromTimeInputFormat = (timeStr) => {
                if (!timeStr || timeStr.length !== 5) return '';
                return timeStr === '00:00' ? '2400' : timeStr.replace(':', '');
            };

            const handleChange = (field) => (e) => {
                const newValue = fromTimeInputFormat(e.target.value);
                updateNestedValue(path ? `${path}.${field}` : field, newValue);
            };

            const handleReset = () => {
                clearNestedValue(path);
            };

            return (
                <div className="mb-3">
                    <label className="form-label fw-medium d-flex justify-between align-items-center">
                        <span>{title}</span>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm ms-2"
                            onClick={handleReset}
                            title="Återställ"
                        >
                            <i className="bi bi-arrow-clockwise"/>
                        </button>
                    </label>
                    <div className="d-flex align-items-center gap-2">
                        <input
                            type="time"
                            value={toTimeInputFormat(value?.ftid)}
                            onChange={handleChange("ftid")}
                            className="form-control w-auto"
                        />
                        <span>–</span>
                        <input
                            type="time"
                            value={toTimeInputFormat(value?.ttid)}
                            onChange={handleChange("ttid")}
                            className="form-control w-auto"
                        />
                    </div>
                </div>

            );
        }

        return (
            <BottomSheet show={show} setShow={setShow}>
                <div className="p-3">
                    <h4 className="mb-3">Tider</h4>

                    <div className="mb-4 p-3 border rounded shadow-sm bg-light-subtle">
                        <h5 className="mb-3">Allmänna</h5>
                        <TimeRangeInput path="" value={hours} title="Öppettider"/>
                    </div>

                    <div className="p-3 border rounded shadow-sm bg-light-subtle">
                        <h5 className="mb-3">Veckodagar</h5>
                        <div className="overflow-y-auto" style={{maxHeight: "40dvh"}}>
                            {["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"].map((item, idx) => (
                                <TimeRangeInput
                                    key={idx}
                                    path={`wday.${idx + 1}`}
                                    value={hours?.wday?.[idx + 1] ?? null}
                                    title={item}
                                />
                            ))}
                        </div>
                    </div>

                    {changed && (
                        <div className="mt-4 text-end">
                            <button className="btn btn-success" onClick={saveHours}>
                                <i className="bi bi-save me-1" /> Spara
                            </button>
                        </div>
                    )}
                </div>
            </BottomSheet>
        );
    }



    function Tab({
                     label, active, onClick
                 }) {
        return (
            <button
                className={`btn ${active ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={onClick}
            >{label}</button>
        );
    }

    function VisibilityTab({item, write, changed, onFieldChange, onSave, onToggleHidden }) {
        const hidden = Number(item.sort) > 899;
        return (
            <>
                <div className="d-flex gap-2 align-items-center mb-3">
                    <h4>{item.text}</h4>
                    {write && (
                        <button className={`btn ${hidden ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                                onClick={onToggleHidden}>
                            <i className={`bi ${hidden ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                    )}
                </div>
                <label className="form-label">Sortering</label>
                <input type="number" className="form-control mb-3" value={item.sort} onChange={onFieldChange('sort')}
                       disabled={!write} />
                {changed && write && (
                    <button className="btn btn-success" onClick={onSave}>
                        <i className="bi bi-save me-2"></i>Spara
                    </button>
                )}
            </>
        );
    }

}
